import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fieldErrorsOf } from '@/lib/forms/fieldErrors';
import { Play } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DRBFMInputData } from '../../../types';
import { parsePartLabelsJson } from '../../../utils/cadPartLabels';
import BasicInfoCard from './input/BasicInfoCard';
import { inputStepSchema } from '@/features/drbfm/schemas/inputStep.schema';
import SpecificationsCard from './input/SpecificationsCard';
import { InputMode, SpecDocInfo } from './input/types';

interface DRBFMInputStepProps {
  inputData: DRBFMInputData;
  onInputChange: (data: Partial<DRBFMInputData>) => void;
  onStartAnalysis: () => void;
  canStart: boolean;
  isLoading: boolean;
  cadEnabled?: boolean | null;
}

const DRBFMInputStep: React.FC<DRBFMInputStepProps> = ({
  inputData,
  onInputChange,
  onStartAnalysis,
  canStart,
  isLoading,
  cadEnabled = null,
}) => {
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [inputMode, setInputMode] = useState<InputMode>(
    !inputData.specOldDocumentId && !inputData.specNewDocumentId
      && (inputData.oldSpecification || inputData.newSpecification) ? 'text' : 'document',
  );
  const textDraft = useRef({
    oldSpecification: inputData.specOldDocumentId ? '' : inputData.oldSpecification,
    newSpecification: inputData.specNewDocumentId ? '' : inputData.newSpecification,
  });
  const [partLabelsJson, setPartLabelsJson] = useState('');
  const [partLabelsError, setPartLabelsError] = useState<string | null>(null);

  const [oldDocInfo, setOldDocInfo] = useState<SpecDocInfo | null>(inputData.specOldDocumentId
    ? { documentId: inputData.specOldDocumentId, fileName: '', preview: inputData.oldSpecification } : null);
  const [newDocInfo, setNewDocInfo] = useState<SpecDocInfo | null>(inputData.specNewDocumentId
    ? { documentId: inputData.specNewDocumentId, fileName: '', preview: inputData.newSpecification } : null);

  const handleInputModeChange = (mode: InputMode) => {
    if (mode === inputMode) return;
    if (mode === 'text') {
      onInputChange({ ...textDraft.current, specOldDocumentId: undefined, specNewDocumentId: undefined });
    } else {
      textDraft.current = { oldSpecification: inputData.oldSpecification, newSpecification: inputData.newSpecification };
      onInputChange({ oldSpecification: oldDocInfo?.preview ?? '', newSpecification: newDocInfo?.preview ?? '',
        specOldDocumentId: oldDocInfo?.documentId, specNewDocumentId: newDocInfo?.documentId });
    }
    setInputMode(mode);
  };

  const clearValidationError = (field: string) => {
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const handleChange = (field: keyof DRBFMInputData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    onInputChange({ [field]: value });

    if (validationErrors[field]) {
      clearValidationError(field);
    }
  };

  const handleOldDocSelect = (documentId: string, content: string, fileName: string, _title: string) => {
    onInputChange({
      specOldDocumentId: documentId,
      oldSpecification: content,
    });
    setOldDocInfo({ documentId, fileName, preview: content });
    if (validationErrors.oldSpecification) {
      clearValidationError('oldSpecification');
    }
  };

  const handleOldDocClear = () => {
    onInputChange({
      specOldDocumentId: undefined,
      oldSpecification: '',
    });
    setOldDocInfo(null);
  };

  const handleNewDocSelect = (documentId: string, content: string, fileName: string, _title: string) => {
    onInputChange({
      specNewDocumentId: documentId,
      newSpecification: content,
    });
    setNewDocInfo({ documentId, fileName, preview: content });
    if (validationErrors.newSpecification) {
      clearValidationError('newSpecification');
    }
  };

  const handleNewDocClear = () => {
    onInputChange({
      specNewDocumentId: undefined,
      newSpecification: '',
    });
    setNewDocInfo(null);
  };

  const handlePartLabelsChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setPartLabelsJson(value);

    try {
      const labels = parsePartLabelsJson(value, t);
      setPartLabelsError(null);
      onInputChange({ predefinedPartLabels: labels.length > 0 ? labels : undefined });
    } catch (err) {
      setPartLabelsError(err instanceof Error ? err.message : t('drbfm.cadLink.parseError', '部位ラベルJSONを読み取れません'));
    }
  };

  // No RHF, deliberately: inputData lives in Redux (shared across wizard steps);
  // the schema supplies rules only, so values keep a single home.
  const handleStartClick = () => {
    const result = inputStepSchema.safeParse({
      itemName: inputData.itemName,
      title: inputData.title ?? '',
      oldSpecification: inputData.oldSpecification,
      newSpecification: inputData.newSpecification,
      // Schema models "nothing selected" as ''; the store leaves it undefined.
      specOldDocumentId: inputData.specOldDocumentId ?? '',
      specNewDocumentId: inputData.specNewDocumentId ?? '',
    });

    const errors: Record<string, string> = { ...fieldErrorsOf(result) };

    // Deliberately outside the schema: this error comes from the JSON parse above.
    if (partLabelsError) {
      errors.predefinedPartLabels = partLabelsError;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    onStartAnalysis();
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="revy-cjk mb-2 text-xl font-medium leading-[1.6]">
          {t('drbfm.input.title')}
        </h2>
        <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
          {t('drbfm.input.description')}
        </p>
      </div>

      <div className="flex flex-col gap-6">
        <BasicInfoCard
          inputData={inputData}
          onInputChange={onInputChange}
          onFieldChange={handleChange}
          isLoading={isLoading}
          cadEnabled={cadEnabled}
          partLabelsJson={partLabelsJson}
          onPartLabelsChange={handlePartLabelsChange}
          partLabelsError={partLabelsError}
          validationErrors={validationErrors}
        />

        <SpecificationsCard
          inputMode={inputMode}
          onInputModeChange={handleInputModeChange}
          inputData={inputData}
          onFieldChange={handleChange}
          oldDocInfo={oldDocInfo}
          newDocInfo={newDocInfo}
          onOldDocSelect={handleOldDocSelect}
          onOldDocClear={handleOldDocClear}
          onNewDocSelect={handleNewDocSelect}
          onNewDocClear={handleNewDocClear}
          validationErrors={validationErrors}
          isLoading={isLoading}
        />

        <Alert variant="info">
          <AlertDescription className="revy-cjk leading-[1.6]">
            {t('drbfm.input.analysisInfo')}
          </AlertDescription>
        </Alert>

        <div className="flex justify-end">
          <Button
            size="lg"
            onClick={handleStartClick}
            disabled={!canStart || isLoading}
          >
            <Play />
            <span className="revy-cjk">
              {isLoading ? t('drbfm.input.starting') : t('drbfm.input.startAnalysis')}
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DRBFMInputStep;
